/**
 * Revenue & Renewal Metrics API Contracts
 *
 * Three super-admin read surfaces over one live HubSpot read:
 * `GET /v1/system/analytics/admin/revenue/{summary,renewals,reconciliation}`.
 *
 * HubSpot is the system of record for MONEY; the platform is the system of
 * record for ENTITLEMENT. These contracts read both and report the delta rather
 * than picking a winner — which is why `reconciliation` exists as a first-class
 * response instead of the other two quietly reconciling behind the reader's back.
 *
 * TWO THINGS EVERY RESPONSE CARRIES (R4). `asOf` and `coverage`. A revenue
 * figure with no coverage count looks complete whether or not it is, and this
 * data is structurally incomplete: on the portal today 16 deals are closed-won
 * but only 13 have a commerce subscription, so three payers have no renewal date
 * anywhere. A list that silently omits them is worse than no list. Nothing in
 * these schemas lets a caller render a number without the count it was built
 * from.
 *
 * MONEY IS A PLAIN NUMBER IN WHOLE US DOLLARS — 12000 means $12,000, never
 * 12000 cents. Fractional values are legitimate (a monthly term annualizes to
 * $5,000.04), so these are not integers. Multi-currency is out of scope: the
 * service EXCLUDES any non-USD record and counts it in
 * `coverage.excludedNonUsd` rather than summing mixed currencies or blanking the
 * whole snapshot over one foreign row.
 *
 * Plan: docs/_archive/2026-10-17-plans-sweep/plans/2026-07-24-003-feat-revenue-metrics-api-plan.md (U3).
 */

import { z } from 'zod'

/**
 * A money amount in whole US dollars. Non-negative: every consumer of these
 * contracts renders revenue, and a negative ARR is a computation bug rather
 * than a credit note — refunds and AR state are explicitly out of scope.
 */
export const RevenueAmountSchema = z.number().nonnegative()

/** A non-negative whole count. */
const CountSchema = z.number().int().nonnegative()

/**
 * How honest the numbers beside this block are (R4).
 *
 * Read it as: "`activeSubscriptions` accounts produced the figures;
 * `closedWonDeals` accounts are known to pay us; the difference is what we could
 * not include." Every field is a count of records, never a money amount — a
 * coverage block that carried its own ARR would just be a second, competing
 * revenue number.
 */
export const RevenueCoverageSchema = z.object({
  /** Active, USD commerce subscriptions the money figures were summed from. */
  activeSubscriptions: CountSchema,
  /** Closed-won deals seen in HubSpot — the wider "who pays us" population. */
  closedWonDeals: CountSchema,
  /**
   * Distinct PAYERS among the active USD subscriptions.
   *
   * `activeSubscriptions` and `closedWonDeals` are RECORD counts, and a record
   * count is not a payer count: one school can hold two active subscriptions, and
   * an initial sale plus a later renewal or expansion are two won deals on one
   * company. Deriving "how many payers" from either field over-counts the moment
   * that happens — which is the normal direction for this data to grow.
   *
   * A record HubSpot associates to no company counts as its own payer rather than
   * being dropped: it cannot be proven to be the same payer as anything else, and
   * dropping it would understate. Those records are also counted in
   * `unmatchedSubscriptions`.
   */
  distinctActivePayers: CountSchema,
  /**
   * Distinct payers across the active USD subscriptions **union** the closed-won
   * USD deals — every party we have evidence pays us.
   *
   * This is the honest denominator for any "N of M payers" qualifier, and
   * `distinctActivePayers` is its numerator. Because the active set is a SUBSET of
   * this union by construction, `distinctPayers >= distinctActivePayers` always
   * holds — so a coverage ratio built from this pair cannot invert into the
   * nonsense of "13 of 12 payers". Pairing a company-distinct denominator with a
   * record-count numerator (or the reverse) reintroduces exactly that inversion;
   * use both or neither.
   */
  distinctPayers: CountSchema,
  /**
   * Closed-won deals whose company matches no active subscription. These are
   * payers with no renewal date anywhere, and the single most important number
   * on this block: it is the size of the hole in `renewals`.
   */
  unmatchedClosedWon: CountSchema,
  /**
   * Active subscriptions whose company matches no `School.hubspotRecordId` (or
   * that HubSpot associates to no company at all). Counted, never dropped:
   * omitting a payer we cannot link would understate ARR while looking complete.
   */
  unmatchedSubscriptions: CountSchema,
  /** Records excluded because their currency is not USD. */
  excludedNonUsd: CountSchema,
  /** Active subscriptions carrying no `hs_next_payment_due_date` — invisible to `renewals`. */
  missingRenewalDate: CountSchema,
  /** Schools on the platform. */
  totalSchools: CountSchema,
  /** Schools with a non-null `hubspotRecordId`. The rest can never join to money. */
  linkedSchools: CountSchema
})

export type RevenueCoverage = z.infer<typeof RevenueCoverageSchema>

/**
 * `?fresh=1` — bypass the service's 10-minute snapshot cache.
 *
 * Strictly `1` or `true`; anything else is false. Deliberately NOT
 * `z.coerce.boolean()`, which reads the string `"0"` as `true` (any non-empty
 * string is truthy) and would turn `?fresh=0` into a forced vendor refresh.
 *
 * A `fresh` request is not a guarantee of a refresh. The HubSpot private-app
 * token is SHARED with support-ticket intake, so the service single-flights the
 * fetch and applies a short cool-down; inside it, `fresh` is served from cache.
 * A reloaded dashboard must not be able to spend the rate-limit budget customer
 * support depends on.
 */
export const RevenueFreshFlagSchema = z
  .string()
  .optional()
  .transform(value => value === '1' || value === 'true')

/**
 * GET /v1/system/analytics/admin/revenue/summary — query params.
 */
export const RevenueSummaryRequestSchema = z.object({
  fresh: RevenueFreshFlagSchema
})

export type RevenueSummaryRequest = z.infer<typeof RevenueSummaryRequestSchema>

/**
 * ARR grouped by plan.
 *
 * `plan` is the PLATFORM plan name, not a HubSpot field — commerce
 * subscriptions carry no plan property, so the only plan notion that exists is
 * the one the platform bills against. `unknown` covers a subscription whose
 * company matched no school, and a matched school with no resolvable
 * default-product subscription. It is a real bucket, not a rounding error: it is
 * how much revenue the platform cannot attribute to a plan.
 */
export const RevenuePlanBreakdownSchema = z.object({
  plan: z.string(),
  accounts: CountSchema,
  arr: RevenueAmountSchema
})

export type RevenuePlanBreakdown = z.infer<typeof RevenuePlanBreakdownSchema>

/**
 * Net-new ARR: closed-won deals grouped by the month they closed (`YYYY-MM`),
 * newest month first. Sourced from deals rather than subscriptions because a
 * subscription has no "when did we win this" date — only a next-payment date.
 *
 * This is NOT ARR over time. It is new business booked per month; it cannot show
 * churn, and it says nothing about what ARR was on any past date. A real trend
 * line needs stored snapshots, which this plan deliberately defers.
 */
export const RevenueMonthClosedSchema = z.object({
  /** `YYYY-MM`, in UTC. */
  month: z.string(),
  deals: CountSchema,
  arr: RevenueAmountSchema
})

export type RevenueMonthClosed = z.infer<typeof RevenueMonthClosedSchema>

/**
 * The closed-won line, reported BESIDE the subscription line and allowed to
 * disagree with it (KTD4).
 *
 * 16 closed-won deals against 13 active subscriptions is a real coverage gap in
 * the source data, and collapsing the two into one reconciled figure would make
 * this endpoint quietly wrong. The top-level `arr` is the subscription-derived
 * truth; this is the wider population it is missing part of.
 */
export const RevenueClosedWonLineSchema = z.object({
  deals: CountSchema,
  arr: RevenueAmountSchema
})

export type RevenueClosedWonLine = z.infer<typeof RevenueClosedWonLineSchema>

/**
 * GET /v1/system/analytics/admin/revenue/summary — response (R1).
 *
 * `arr`, `mrr`, `averageAcv`, and `payingAccounts` all describe the SAME
 * population: active, USD commerce subscriptions. `averageAcv` is `arr ÷
 * payingAccounts`, and 0 when there are none — not null, because a dashboard
 * dividing by zero is a worse failure than a zero.
 */
export const RevenueSummaryResponseSchema = z.object({
  asOf: z.string(),
  arr: RevenueAmountSchema,
  mrr: RevenueAmountSchema,
  averageAcv: RevenueAmountSchema,
  payingAccounts: CountSchema,
  closedWon: RevenueClosedWonLineSchema,
  byPlan: z.array(RevenuePlanBreakdownSchema),
  netNewArrByMonthClosed: z.array(RevenueMonthClosedSchema),
  coverage: RevenueCoverageSchema
})

export type RevenueSummaryResponse = z.infer<typeof RevenueSummaryResponseSchema>

/**
 * The three renewal windows, in days. A fixed set rather than an open integer:
 * these feed a dashboard's three tabs, and an arbitrary window would be a
 * cache-key and vendor-budget surface for no product gain.
 */
export const RevenueRenewalWindowSchema = z.union([z.literal(30), z.literal(60), z.literal(90)])

export type RevenueRenewalWindow = z.infer<typeof RevenueRenewalWindowSchema>

/**
 * GET /v1/system/analytics/admin/revenue/renewals — query params.
 *
 * `window` defaults to 30. Anything outside {30, 60, 90} is a 400 at this layer;
 * the service never sees it.
 */
export const RevenueRenewalsRequestSchema = z.object({
  window: z
    .enum(['30', '60', '90'])
    .optional()
    .default('30')
    .transform(value => Number(value) as RevenueRenewalWindow),
  fresh: RevenueFreshFlagSchema
})

export type RevenueRenewalsRequest = z.infer<typeof RevenueRenewalsRequestSchema>

/**
 * One upcoming renewal, joined to its platform school where possible.
 *
 * The school fields are NULLABLE by design. A subscription whose company matches
 * no `School.hubspotRecordId` still renews, and still belongs on this list —
 * dropping it would hide a real payment date because of a CRM linking gap. When
 * they are null, `hubspotCompanyId` is the thread an operator pulls to fix the
 * link.
 *
 * `daysOut` is whole days from "now" to `nextDueDate`, and is never negative:
 * this list is forward-looking, and a past-due subscription surfaces in
 * `reconciliation` under `unpaidCommerceStatus` instead.
 */
export const RevenueRenewalRowSchema = z.object({
  subscriptionId: z.string(),
  /** ISO date as HubSpot carries it (`hs_next_payment_due_date`). */
  nextDueDate: z.string(),
  daysOut: CountSchema,
  mrr: RevenueAmountSchema,
  arr: RevenueAmountSchema,
  schoolPublicId: z.string().nullable(),
  schoolName: z.string().nullable(),
  hubspotCompanyId: z.string().nullable()
})

export type RevenueRenewalRow = z.infer<typeof RevenueRenewalRowSchema>

/**
 * GET /v1/system/analytics/admin/revenue/renewals — response (R2).
 *
 * `renewals` is sorted soonest-first. `window` is echoed back so a client
 * rendering a cached response cannot mislabel which window it is showing.
 */
export const RevenueRenewalsResponseSchema = z.object({
  asOf: z.string(),
  window: RevenueRenewalWindowSchema,
  renewals: z.array(RevenueRenewalRowSchema),
  coverage: RevenueCoverageSchema
})

export type RevenueRenewalsResponse = z.infer<typeof RevenueRenewalsResponseSchema>

/**
 * GET /v1/system/analytics/admin/revenue/reconciliation — query params.
 */
export const RevenueReconciliationRequestSchema = z.object({
  fresh: RevenueFreshFlagSchema
})

export type RevenueReconciliationRequest = z.infer<typeof RevenueReconciliationRequestSchema>

/**
 * A school where HubSpot and the platform disagree about whether it pays us.
 *
 * `platformStatus` is the lowercased primary-subscription status (`trial`,
 * `paying`, `churned`, …) or null when the school has no resolvable
 * default-product subscription at all. `reason` is a short human sentence — this
 * report is read by an operator deciding what to fix, and a bare bucket name
 * does not tell them what they are looking at.
 */
export const RevenueReconciliationSchoolSchema = z.object({
  schoolPublicId: z.string(),
  schoolName: z.string(),
  platformStatus: z.string().nullable(),
  hubspotCompanyId: z.string().nullable(),
  reason: z.string()
})

export type RevenueReconciliationSchool = z.infer<typeof RevenueReconciliationSchoolSchema>

/**
 * A school with no `hubspotRecordId`. It can never join to money, in either
 * direction — so it is neither a disagreement nor an agreement, and gets its own
 * bucket rather than being scored as "not paying".
 */
export const RevenueUnlinkedSchoolSchema = z.object({
  schoolPublicId: z.string(),
  schoolName: z.string(),
  platformStatus: z.string().nullable()
})

export type RevenueUnlinkedSchool = z.infer<typeof RevenueUnlinkedSchoolSchema>

/** A commerce subscription HubSpot does not consider `active` — an at-risk payer. */
export const RevenueUnpaidSubscriptionSchema = z.object({
  subscriptionId: z.string(),
  /** HubSpot's own `hs_status`, passed through (`unpaid`, `expired`, …). */
  status: z.string(),
  schoolPublicId: z.string().nullable(),
  schoolName: z.string().nullable()
})

export type RevenueUnpaidSubscription = z.infer<typeof RevenueUnpaidSubscriptionSchema>

/**
 * GET /v1/system/analytics/admin/revenue/reconciliation — response (R3).
 *
 * READ-ONLY AND PROPOSE-ONLY (KTD8). This names schools and reasons; it never
 * flips `Subscription.status`. The only sanctioned write path to a subscription
 * remains the lifecycle service, run by a human.
 *
 * THIS REPORT HAS NO MEMORY. With no persistence, every call recomputes drift
 * from scratch, so nothing here distinguishes a school that fell out of sync
 * today from one that has been wrong for three months, and nothing records what
 * an operator already handled. It answers "what is wrong now" — never "what is
 * new". Closing that gap needs stored snapshots and is deliberately deferred.
 */
export const RevenueReconciliationResponseSchema = z.object({
  asOf: z.string(),
  /** Won in HubSpot, not `paying` on the platform — the flip an operator should consider. */
  hubspotPaidPlatformTrial: z.array(RevenueReconciliationSchoolSchema),
  /** `paying` on the platform with no won deal — either a stale flip or a missing CRM record. */
  platformPayingNoWonDeal: z.array(RevenueReconciliationSchoolSchema),
  unlinkedSchools: z.array(RevenueUnlinkedSchoolSchema),
  unpaidCommerceStatus: z.array(RevenueUnpaidSubscriptionSchema),
  coverage: RevenueCoverageSchema
})

export type RevenueReconciliationResponse = z.infer<typeof RevenueReconciliationResponseSchema>
